import { useState } from "react";
import { HelpCircle, Plus, Minus, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type FaqItem = {
  q: string;
  a: string;
  linkLabel?: string;
  linkHref?: string;
};
const FAQS: FaqItem[] = [{
  q: "How much does a new garage door installation cost in Sydney?",
  a: "Installation pricing varies based on door type, size, brand, finish, insulation, motor inclusion, and removal needs. A basic 2.4m roller for a granny flat is a very different job than a 5m custom timber-look sectional with smart motor — a single number online would be misleading. We provide a fixed all-inclusive quote after a free on-site measure: door, install, removal, disposal, and warranty all in one transparent number with no surprise extras.",
  linkLabel: "See our pricing pledges",
  linkHref: "#installation-pricing-heading"
}, {
  q: "How long does the whole installation process take from start to finish?",
  a: "Two to four weeks all-in. Day 0 — phone consult and book your measure (usually within 48 hours). Day 1–3 — 60–90 minute on-site measure with written fixed-price quote left on the day. Day 7–21 — door manufactured once you approve (standard stock 7–14 days, custom paint 14–21 days, heritage replicas 21–28 days). Day 14–28 — install day, single day, usually 4–6 hours. We track every step and coordinate manufacturer lead times.",
  linkLabel: "See our installation process",
  linkHref: "#installation-process-heading"
}, {
  q: "Do you remove and dispose of my old garage door?",
  a: "Yes — included in every installation quote with no separate charge and no hire skip required. We safely drum-roll the cables (broken springs and tensioned cables are dangerous to handle), dismantle the old door piece by piece, and properly dispose of all materials. The only thing left behind on install day is your new door — your driveway is left exactly as we found it."
}, {
  q: "What garage door brands do you install?",
  a: "We install factory-direct from every major Australian and European brand: B&D, Steel-Line, Gliderol, Centurion, 4D Industrial, and Hörmann. Every door comes with full manufacturer warranty in your name. We're not tied to any one supplier — if you ask, we'll quote two or three brands side-by-side for the same opening so you can compare features, lead times, and pricing directly. No bias, no kickbacks."
}, {
  q: "Can I get a custom size or finish to match my home?",
  a: "Yes — custom sizes (oversized, undersized, non-standard openings), custom paint colours matched to your render or weatherboard, timber-look skin overlays, heritage replica panels, and custom hardware. Lead times for custom finishes typically run 14–28 days from measure to install day. Bring or send us photos of your home and existing exterior — we'll walk you through what's possible within your budget."
}, {
  q: "Do you supply and install the motor / opener too?",
  a: "Yes. We supply and install openers from BOSS Forza, Merlin, ATA, Steel-Line, B&D, and DoorWorks — the same brands we repair every day, so we know which models last and which to avoid. Motors can be quoted as part of the door package or added later. Most new installs include a smart-enabled motor with Wi-Fi connectivity, soft-start, and manufacturer warranty of 2–5 years depending on model.",
  linkLabel: "View motors & openers",
  linkHref: "/garage-door-motor-repair-sydney/"
}, {
  q: "What does the workmanship warranty cover?",
  a: "Every installation comes with a 12-month written workmanship warranty — separate to and on top of the manufacturer's warranty on the door itself (typically 5–10 years) and motor (typically 2–5 years). If anything we touched fails within the workmanship period — install fitment, sealing, balance, electrical wiring, motor programming — we come back and fix it free. We hand over the warranty card with our direct number on install day, not just an emailed receipt."
}];
export default function InstallationFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a
      }
    }))
  };
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="installation-faq-heading">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      <div aria-hidden className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        <div className="flex items-center justify-between gap-6 mb-12 md:mb-16 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <HelpCircle className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              FAQ &mdash; Installation
            </span>
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            7 Questions · Straight Answers
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">

          {/* LEFT — sticky */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <h2 id="installation-faq-heading" className="font-extrabold mb-6" style={{
            fontFamily: FONT_HEAD,
            color: NAVY,
            fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.03em"
          }}>
              The install questions{" "}
              <span style={{
              color: GOLD_DARK
            }}>we hear most often.</span>
            </h2>

            <p className="text-[15px] leading-[1.65] mb-8 max-w-md" style={{
            color: INK_MUTED
          }}>
              Straight answers from a family-run installer that&rsquo;s put
              up thousands of doors across Sydney. If your question
              isn&rsquo;t here, a 5-minute phone call usually solves it.
            </p>

            <div className="relative overflow-hidden rounded-2xl border p-6" style={{
            backgroundColor: PAPER,
            borderColor: `${NAVY}14`
          }}>
              <div aria-hidden className="absolute top-0 left-6 right-6 h-px" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}40 100%)`
            }} />

              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-xl" style={{
                backgroundColor: `${GOLD}1F`,
                border: `1px solid ${GOLD_DARK}40`
              }}>
                  <MessageCircle className="h-4 w-4" strokeWidth={2} style={{
                  color: GOLD_DARK
                } as React.CSSProperties} />
                </div>
                <div>
                  <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-1" style={{
                  color: GOLD_DARK
                }}>
                    Question not listed?
                  </div>
                  <p className="font-bold text-[15px] leading-snug" style={{
                  color: NAVY,
                  fontFamily: FONT_HEAD
                }}>
                    Tell us about your project &mdash; we&rsquo;ll quote
                    properly after a free measure.
                  </p>
                </div>
              </div>

              <a href="tel:0485000933" className="group inline-flex items-center gap-2 text-[14px] font-bold transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => e.currentTarget.style.color = GOLD_DARK} onMouseLeave={e => e.currentTarget.style.color = NAVY}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Call 0485 000 933
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>
            </div>
          </div>

          {/* RIGHT — accordion */}
          <div className="lg:col-span-7">
            <ul className="space-y-3">
              {FAQS.map((faq, idx) => <FaqRow key={faq.q} faq={faq} num={String(idx + 1).padStart(2, "0")} isOpen={openIndex === idx} onToggle={() => setOpenIndex(openIndex === idx ? null : idx)} />)}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function FaqRow({
  faq,
  num,
  isOpen,
  onToggle
}: {
  faq: FaqItem;
  num: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <li className="rounded-2xl border overflow-hidden transition-all duration-300" style={{
    backgroundColor: PAPER,
    borderColor: isOpen ? `${GOLD_DARK}66` : `${NAVY}14`,
    boxShadow: isOpen ? `0 12px 36px -12px ${GOLD_DARK}33` : `0 4px 14px -8px ${NAVY}1F`
  }}>
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="group w-full flex items-start gap-4 md:gap-5 text-left px-5 md:px-6 py-5">
        <span className="flex-shrink-0 text-base font-extrabold tabular-nums leading-none mt-1" style={{
        fontFamily: FONT_HEAD,
        color: isOpen ? GOLD_DARK : INK_MUTED,
        transition: "color 200ms"
      }}>
          {num}
        </span>

        <span className="flex-1 font-bold pr-3" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "clamp(1rem, 1.5vw, 1.125rem)",
        lineHeight: 1.35,
        letterSpacing: "-0.012em"
      }}>
          {faq.q}
        </span>

        <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full transition-all duration-300" style={{
        backgroundColor: isOpen ? GOLD : "transparent",
        border: isOpen ? `1px solid ${GOLD}` : `1px solid ${INK_MUTED}40`
      }} aria-hidden>
          {isOpen ? <Minus className="h-4 w-4" strokeWidth={2.5} style={{
          color: NAVY
        } as React.CSSProperties} /> : <Plus className="h-4 w-4" strokeWidth={2.5} style={{
          color: INK_MUTED
        } as React.CSSProperties} />}
        </span>
      </button>

      {/* Body */}
      <div className="overflow-hidden transition-all duration-300" style={{
      maxHeight: isOpen ? "600px" : "0",
      opacity: isOpen ? 1 : 0
    }}>
        <div className="px-5 md:px-6 pb-6 pt-1 grid grid-cols-1 md:grid-cols-[44px_1fr] gap-x-5">
          <div aria-hidden className="hidden md:block w-full h-full" style={{
          borderTop: `1px solid ${GOLD_DARK}33`,
          marginTop: 0
        }} />
          <div className="md:pt-4">
            <div aria-hidden className="md:hidden w-full h-px mb-4" style={{
            backgroundColor: `${GOLD_DARK}33`
          }} />
            <p className="text-[14.5px] leading-[1.7]" style={{
            color: INK
          }}>
              {faq.a}
            </p>

            {faq.linkLabel && faq.linkHref && <a href={faq.linkHref} className="group mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold tracking-[0.16em] uppercase transition-colors" style={{
            color: GOLD_DARK
          }} onMouseEnter={e => e.currentTarget.style.color = NAVY} onMouseLeave={e => e.currentTarget.style.color = GOLD_DARK}>
                &mdash; {faq.linkLabel}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>}
          </div>
        </div>
      </div>
    </li>;
}